import React, { useState, useEffect, useRef } from 'react';
import { setAgentStatus } from '../services/api';
import './AgentCard.css';

const WRAP_UP_DURATIONS = [30, 60, 90, 120, 180];

const AgentCard = ({ agent, viewMode = 'grid', isCurrentUser = false, onClick, onStatusChange }) => {
  const [wrapUpActive, setWrapUpActive] = useState(false);
  const [wrapUpRemaining, setWrapUpRemaining] = useState(0);
  const [wrapUpDuration, setWrapUpDuration] = useState(60);
  const [wrapUpError, setWrapUpError] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startWrapUp = async () => {
    setWrapUpError(null);
    try {
      await setAgentStatus(agent.id, 'Wrap-up');
      setWrapUpActive(true);
      setWrapUpRemaining(wrapUpDuration);
      if (onStatusChange) onStatusChange();

      timerRef.current = setInterval(() => {
        setWrapUpRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            setAgentStatus(agent.id, 'Available').then(() => {
              setWrapUpActive(false);
              if (onStatusChange) onStatusChange();
            }).catch(err => {
              console.error('Failed to set Available:', err);
              setWrapUpActive(false);
            });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setWrapUpError(err.response?.data?.error || 'Failed to start wrap-up');
    }
  };

  const endWrapUpEarly = async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    try {
      await setAgentStatus(agent.id, 'Available');
      setWrapUpActive(false);
      setWrapUpRemaining(0);
      if (onStatusChange) onStatusChange();
    } catch (err) {
      setWrapUpError(err.response?.data?.error || 'Failed to end wrap-up');
    }
  };

  const formatTimer = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };
  const getStatusColor = (status) => {
    const statusColors = {
      'available': '#10b981',
      'in-call': '#3b82f6',
      'wrap-up': '#8b5cf6',
      'away': '#f59e0b',
      'unavailable': '#ef4444',
      'offline': '#6b7280',
      'busy': '#ef4444'
    };
    return statusColors[status?.toLowerCase()] || '#6b7280';
  };

  const getStatusIcon = (status) => {
    const statusName = status?.toLowerCase() || '';
    
    if (statusName === 'available') {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    
    if (statusName === 'in-call') {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      );
    }
    
    if (statusName === 'wrap-up') {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    
    if (statusName === 'away') {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    
    if (statusName === 'offline') {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="12" r="9" />
      </svg>
    );
  };

  const formatDuration = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const now = new Date();
    const statusTime = new Date(timestamp);
    const diffMs = now - statusTime;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;
    
    if (diffHours < 24) {
      return remainingMins > 0 ? `${diffHours}h ${remainingMins}m ago` : `${diffHours}h ago`;
    }
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const statusColor = getStatusColor(agent.status?.name);
  const statusName = agent.status?.name || 'Unknown';

  // Get indicator color based on status
  const getIndicatorColor = () => {
    if (!agent.online) return '#6b7280'; // Grey for offline
    
    const status = agent.status?.name?.toLowerCase();
    
    if (status === 'in-call' || status === 'in call') {
      return '#ef4444'; // Red for in-call
    } else if (status === 'break' || status === 'away') {
      return '#f59e0b'; // Yellow/Amber for break/away
    } else if (status === 'unavailable') {
      return '#6b7280'; // Grey for unavailable
    } else if (status === 'available') {
      return '#10b981'; // Green for available
    } else if (status === 'wrap-up' || status === 'wrapup') {
      return '#8b5cf6'; // Purple for wrap-up
    }
    
    return '#10b981'; // Default green for online
  };

  return (
    <div 
      className={`agent-card ${agent.online ? 'online' : 'offline'} ${viewMode === 'list' ? 'list-mode' : 'grid-mode'} ${onClick ? 'clickable' : 'view-only'} ${isCurrentUser ? 'current-user' : ''}`}
      onClick={() => onClick && onClick(agent)}
      title={!onClick ? 'You can only view your own detailed data' : 'Click to view details'}
    >
      {isCurrentUser && (
        <div className="current-user-badge">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
          </svg>
          You
        </div>
      )}
      <div className="agent-card-header">
        <div className="agent-avatar-container">
          <img 
            src={agent.avatar_url} 
            alt={`${agent.first_name} ${agent.last_name}`}
            className="agent-avatar"
          />
          <div 
            className="online-indicator" 
            style={{ backgroundColor: getIndicatorColor() }}
          />
        </div>
        
        <div className="agent-info">
          <h3 className="agent-name">
            {agent.first_name} {agent.last_name}
          </h3>
          {agent.roles && agent.roles.length > 0 && (() => {
            // Org is decided server-side (RBDCAI-1070); fall back to email domain only if absent.
            const isDyopath = agent.org
              ? agent.org === 'dyopath'
              : (agent.email?.split('@')[1]?.toLowerCase() || '') === 'dyopath.com';
            const org = isDyopath ? 'Dyopath' : 'Redbull';
            const isAdmin = agent.roles.some(r => r.toLowerCase().includes('admin'));
            const isManager = agent.roles.some(r => r.toLowerCase().includes('manager') && !r.toLowerCase().includes('data'));
            const displayRole = isAdmin ? `${org} Admin` : isManager ? `${org} Manager` : `${org} Agent`;
            return (
              <div className="agent-role-badges">
                <span className={`role-badge ${isDyopath ? 'role-dyopath' : 'role-redbull'}`}>{displayRole}</span>
              </div>
            );
          })()}
        </div>
      </div>

      <div className="agent-status-badge" style={{ backgroundColor: statusColor }}>
        <div className="status-icon">
          {getStatusIcon(statusName)}
        </div>
        <span className="status-text">{statusName}</span>
        {agent.wrap_up && (
          <span className="wrap-up-indicator">Wrap-up</span>
        )}
      </div>

      {isCurrentUser && agent.online && (
        <div className="wrapup-timer-control" onClick={(e) => e.stopPropagation()}>
          {wrapUpActive ? (
            <div className="wrapup-active">
              <div className="wrapup-countdown">
                <div className="wrapup-progress-bar">
                  <div
                    className="wrapup-progress-fill"
                    style={{ width: `${(wrapUpRemaining / wrapUpDuration) * 100}%` }}
                  />
                </div>
                <span className="wrapup-time-left">{formatTimer(wrapUpRemaining)}</span>
              </div>
              <button className="wrapup-end-btn" onClick={endWrapUpEarly}>
                End Wrap-up
              </button>
            </div>
          ) : (
            <div className="wrapup-start">
              <select
                className="wrapup-duration-select"
                value={wrapUpDuration}
                onChange={(e) => setWrapUpDuration(Number(e.target.value))}
                onClick={(e) => e.stopPropagation()}
              >
                {WRAP_UP_DURATIONS.map(d => (
                  <option key={d} value={d}>{d < 60 ? `${d}s` : `${d / 60}m${d % 60 ? ` ${d % 60}s` : ''}`}</option>
                ))}
              </select>
              <button className="wrapup-start-btn" onClick={startWrapUp}>
                Start Wrap-up
              </button>
            </div>
          )}
          {wrapUpError && <div className="wrapup-error">{wrapUpError}</div>}
        </div>
      )}

      <div className="agent-details">
        <div className="detail-row">
          <span className="detail-label">Status Duration:</span>
          <span className="detail-value">{formatDuration(agent.status_updated_at)}</span>
        </div>
        
        <div className="detail-row">
          <span className="detail-label">Last Login:</span>
          <span className="detail-value">{formatTime(agent.last_login_time)}</span>
        </div>
        
        {agent.location && (
          <div className="detail-row">
            <span className="detail-label">Location:</span>
            <span className="detail-value">{agent.location}</span>
          </div>
        )}
      </div>

      <div className="agent-metrics">
        <div className="metric">
          <div className="metric-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </div>
          <div className="metric-content">
            <div className="metric-value">{agent.calls_24h || 0}</div>
            <div className="metric-label">Calls (24h)</div>
          </div>
        </div>

        <div className="metric">
          <div className="metric-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <div className="metric-content">
            <div className="metric-value">{agent.chats_24h || 0}</div>
            <div className="metric-label">Chats (24h)</div>
          </div>
        </div>
      </div>

      {agent.teams && agent.teams.length > 0 && (
        <div className="agent-teams">
          <div className="teams-label">Teams:</div>
          <div className="teams-list">
            {agent.teams.map(team => (
              <span key={team.id} className="team-badge">
                {team.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {agent.channels && agent.channels.length > 0 && (
        <div className="agent-channels">
          {agent.channels.map((channel, idx) => (
            <span key={idx} className="channel-tag">
              {channel.channel_type?.replace('_', ' ')}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentCard;

